import { JoggCart, JoggCartLineItem } from "@jog/types"

import { JC_FRIEND_SKU, convertCentsToEuros, transGtmId, typedBoolean } from "../helper"
import { gtmPush } from "./index"

const handleProduct = (product: JoggCartLineItem) => {
    if ("categoryAsUrl" in product) {
        return {
            name: product.name?.en,
            category: product.categoryAsUrl,
        }
    }
    return {
        name: product.enName,
        category: product.path.split("/").slice(0, -2).filter(typedBoolean).join("/"),
    }
}

export const gtmRemoveFromCart = (product: JoggCartLineItem, quantity = 1) => {
    if (!product) return
    const { prices, brand, articleName, colorCode } = product
    gtmPush({
        event: "removeFromCart",
        ecommerce: {
            remove: {
                products: [
                    {
                        id: transGtmId({ articleName, colorCode }),
                        price: parseFloat(convertCentsToEuros(prices.firstPriceInTheCart.centAmount)),
                        brand,
                        variant: "",
                        quantity,
                        ...handleProduct(product),
                    },
                ],
            },
        },
    })
}

function gtmAddToCart(
    actionfieldList: string,
    product: { name: string; id: string; price: number; brand: string; category: string },
    quantity = 1
) {
    gtmPush({
        event: "addToCart",
        ecommerce: {
            currencyCode: "EUR",
            actionfield: { list: actionfieldList },
            add: { products: [{ ...product, variant: "", quantity, list: "" }] },
        },
    })
}

export const gtmAddLineItemToCart = (product: JoggCartLineItem, quantity = 1) => {
    if (!product) return
    gtmAddToCart(
        "",
        {
            brand: "",
            id: product.id,
            price: parseFloat(convertCentsToEuros(product.prices.firstPriceInTheCart.centAmount)),
            ...handleProduct(product),
        },
        quantity
    )
}

export const gtmCartPageBasketOverview = (lineItems: JoggCart["lineItems"]) => {
    if (!lineItems?.length) return
    const products = lineItems
        .filter(typedBoolean)
        .map((product) => ({ id: transGtmId({ articleName: product.articleName, colorCode: product.colorCode }) }))
    gtmPush({ event: "BasketOverview", products })
}

export function gtmCheckoutCreateDataLayer(
    isLogged: boolean,
    productsData: JoggCart["lineItems"],
    step: number,
    optionText?: string,
    email = ""
) {
    if (!productsData) return
    const products = productsData
        .filter(typedBoolean)
        .filter((i) => i.sku !== JC_FRIEND_SKU)
        .map((product) => ({
            id: transGtmId({ articleName: product.articleName, colorCode: product.colorCode }),
            price: parseFloat(convertCentsToEuros(product.prices.firstPriceInTheCart.centAmount)),
            brand: "",
            variant: "",
            ...handleProduct(product),
        }))
    gtmPush({
        event: "checkout",
        ecommerce: {
            checkout: {
                actionField: { step, option: optionText || (isLogged ? "logged in" : "logged out"), email },
                products,
            },
        },
    })
}

export const gtmCheckoutBillingForm = (
    isLogged: boolean,
    productsData: JoggCart["lineItems"],
    isUserLoggedWithAmplify = false,
    isRegistration = false
) => {
    const step = 4
    if (isLogged && !isUserLoggedWithAmplify) {
        // user was already logged when he came to checkout. Send also data about gtm step 2 and 3 he didn't see
        gtmCheckoutCreateDataLayer(isLogged, productsData, 2)
        gtmCheckoutCreateDataLayer(isLogged, productsData, 3)
        gtmCheckoutCreateDataLayer(isLogged, productsData, step, "logged in")
        return
    }
    if (isUserLoggedWithAmplify) {
        // user logged from Amplify on checkout (gtm step 3)
        gtmCheckoutCreateDataLayer(isLogged, productsData, step, "logged in")
        return
    }
    gtmCheckoutCreateDataLayer(isLogged, productsData, 3)
    gtmCheckoutCreateDataLayer(
        isLogged,
        productsData,
        step,
        isRegistration ? "nieuw account aangemaakt" : "bestellen zonder account"
    )
}

export const gtmCartOverview = (lineItems: JoggCart["lineItems"]) => {
    const products = lineItems.filter(typedBoolean).map((product) => ({
        id: transGtmId({ articleName: product.articleName, colorCode: product.colorCode }),
    }))

    gtmPush({ event: "BasketOverview", products })
}

export const gtmCartRecovery = (cart?: JoggCart) => {
    const token = cart?.recovery?.token
    if (!token) return
    gtmPush({ event: "cartRecovery", recovery: { link: `${window.location.origin}/cart?recovery=${token}` } })
}
